import { tetriToDecimalString } from '@/utils/money';
import type {
    LocaleCode,
    VatBreakdownLine,
    VatContributingEntry,
    VatReport,
    VatRole,
    VatSide,
    VatSideRole,
    VatWarning,
} from '@/api/models';

/**
 * Wire ⇄ domain mapping for the VAT report (bos-api).
 *
 * **This is the only file that knows the server's field names.** Everything
 * downstream — the service, the query hooks, the page hook, the tables, the
 * settlement panel — sees the domain shapes in `@/api/models/Vat`. If the wire
 * disagrees with what is assumed here, this file is the whole of the fix. Same
 * seam, and the same reason, as `accountsMapping`, `journalMapping` and
 * `statementsMapping`.
 *
 * The client's original frontend-authored contract diverged from the shipped API
 * in nearly every field. Measured against `/openapi/v1.json` and live responses
 * on 2026-07-30:
 *
 * | domain (as the mock had it) | wire |
 * | --- | --- |
 * | `meta: { from, to, currency }` | flat `dateFrom` / `dateTo`; **no currency** (it lives on `/ledger/settings`) |
 * | two sides, `output` + `input` | **one flat `accounts[]`**, each carrying `role: NONE\|INPUT\|OUTPUT` — the split happens here |
 * | `output.totalTetri` / `.total` | `outputVatTetri` / `outputVat` |
 * | `input.totalTetri` / `.total` | `inputVatTetri` / `inputVat` |
 * | `netTetri` / `net` | `netPayableTetri` / `netPayable` |
 * | line `accountCode` | `code` |
 * | line `accountName` (a locale map) | `name` — a **string, already resolved** to the requested `locale` |
 * | line `amountTetri` / `amount` | `vatAmountTetri` / `vatAmount` — and `vatAmount` is a JSON **number**, not a decimal string |
 * | *(absent)* | `entries[]` — the journal entries behind each account's figure |
 * | *(absent)* | `settlementAccountCode`, `settlementAccountBalanceTetri` / `…Balance` |
 * | `warnings[]` | **absent entirely** — no field, and no VAT counterpart to `/statements/plausibility` |
 * | `POST /vat/settlement/prepare?from=&to=` | `POST /ledger/vat/settlement` with a JSON **body** `{ dateFrom, dateTo, date, locale }` |
 * | `{ draft, alreadyExisted }` | a bare `JournalEntryResponse` — **no envelope, and no duplicate flag** |
 * | query `from` / `to` | `dateFrom` / `dateTo` |
 *
 * Five properties of the wire this mapping preserves rather than "corrects",
 * because correcting any of them would misreport the position:
 *
 * 1. **The role tag decides the side, and nothing else does.** `accounts[]`
 *    arrives flat; {@link mapVatReport} partitions it on `role` alone. No code
 *    range, no parent-account inference, no assumption that a code implies a
 *    side. Verified live in both directions: re-tagging 2120 `OUTPUT`→`INPUT`
 *    moved ₾3,200 across the report, and renumbering the same account 2120→2125
 *    changed nothing at all.
 * 2. **`vatAmountTetri` can be negative.** It is the account's movement in the
 *    direction its role expects, not a magnitude — an account tagged against the
 *    way its balance actually moves reports a negative contribution. Taking the
 *    absolute value would turn a contra movement into a charge.
 * 3. **The totals are the server's, not a sum of the lines.** They are carried
 *    through untouched, so a line this mapping had to drop cannot silently
 *    change a total.
 * 4. **`netPayableTetri` is the server's too**, and it is exactly
 *    `outputVatTetri − inputVatTetri` (measured). Recomputing it here would add a
 *    second opinion about the one figure the screen exists to state.
 * 5. **A zero-amount side is a real result.** Tagged accounts with no movement
 *    still arrive, and are kept: a period with no VAT activity is a legitimate
 *    all-zero report, not an empty state.
 *
 * A line that cannot be mapped is **dropped, not guessed at** — a VAT figure
 * attributed to the wrong account is a reporting error, and one drilling to the
 * wrong ledger rows is worse than one that is missing. {@link mapVatReport}
 * reports what it dropped.
 */

// ── wire shapes ─────────────────────────────────────────────────────────────

export type VatContributingEntryWire = {
    entryId?: string;
    entryNumber?: number | null;
    date?: string;
    description?: string | null;
    debit?: number | null;
    debitTetri?: number;
    credit?: number | null;
    creditTetri?: number;
};

export type VatAccountBreakdownWire = {
    accountId?: string;
    code?: string;
    name?: string;
    role?: string;
    vatAmountTetri?: number;
    vatAmount?: number | null;
    entries?: VatContributingEntryWire[] | null;
};

/**
 * The wire has no warnings field today. Declared optional so a future one is
 * read rather than needing this file changed in a hurry — see
 * {@link mapVatWarning}.
 */
export type VatWarningWire = {
    rule?: string;
    message?: string;
    accountId?: string | null;
    accountCode?: string | null;
};

export type VatReportWire = {
    dateFrom?: string;
    dateTo?: string;
    locale?: string | null;
    outputVatTetri?: number;
    outputVat?: number | null;
    inputVatTetri?: number;
    inputVat?: number | null;
    netPayableTetri?: number;
    netPayable?: number | null;
    settlementAccountCode?: string | null;
    settlementAccountBalanceTetri?: number;
    settlementAccountBalance?: number | null;
    accounts?: VatAccountBreakdownWire[] | null;
    warnings?: VatWarningWire[] | null;
};

// ── primitives ──────────────────────────────────────────────────────────────

const isFiniteInt = (value: unknown): value is number =>
    typeof value === 'number' && Number.isFinite(value);

/**
 * The integer is authoritative (the project money contract), so the decimal
 * companion is only ever a display echo — and here the wire sends it as a JSON
 * **number** (`3200` for `320000` tetri), not the decimal string the client
 * originally assumed. Deriving the echo from the integer sidesteps both the type
 * mismatch and any float the companion may have picked up.
 */
const echoOf = (tetri: number): string => tetriToDecimalString(tetri);

const intOr = (value: unknown, fallback: number): number =>
    isFiniteInt(value) ? Math.trunc(value) : fallback;

const SIDE_ROLES: readonly string[] = ['INPUT', 'OUTPUT'];

const ROLES: readonly string[] = ['NONE', 'INPUT', 'OUTPUT'];

/** `null` for anything that is not one of the two sides — `NONE` included. */
const sideRoleOf = (wire: string | undefined): VatSideRole | null =>
    typeof wire === 'string' && SIDE_ROLES.includes(wire)
        ? (wire as VatSideRole)
        : null;

/** Exposed for the account edit path, which needs the full three-value tag. */
export const vatRoleOf = (wire: unknown): VatRole =>
    typeof wire === 'string' && ROLES.includes(wire)
        ? (wire as VatRole)
        : 'NONE';

const localeOf = (wire: string | null | undefined): LocaleCode =>
    wire === 'ka' ? 'ka' : 'en';

// ── contributing entries ────────────────────────────────────────────────────

/**
 * `null` when the entry could not be identified.
 *
 * An unidentifiable contribution is dropped rather than kept with a blank id:
 * these exist to answer "which entries make up this figure", and a row that
 * cannot be opened answers nothing while still counting towards the line looking
 * drillable.
 */
export const mapVatContributingEntry = (
    wire: VatContributingEntryWire,
): VatContributingEntry | null => {
    if (!wire.entryId) {
        return null;
    }
    const debitTetri = intOr(wire.debitTetri, 0);
    const creditTetri = intOr(wire.creditTetri, 0);
    return {
        entryId: wire.entryId,
        entryNumber: isFiniteInt(wire.entryNumber) ? wire.entryNumber : null,
        date: wire.date ?? '',
        description: wire.description ?? '',
        debit: echoOf(debitTetri),
        debitTetri,
        credit: echoOf(creditTetri),
        creditTetri,
    };
};

// ── breakdown lines ─────────────────────────────────────────────────────────

/**
 * `null` when the account cannot be placed on a side with confidence — no id, no
 * readable figure, or a role that is not `INPUT`/`OUTPUT`.
 *
 * `NONE`-tagged accounts land here too and are meant to: they are not part of
 * either side. The settlement account is the obvious one — it receives the net,
 * it does not contribute to it.
 */
export const mapVatBreakdownLine = (
    wire: VatAccountBreakdownWire,
): VatBreakdownLine | null => {
    const role = sideRoleOf(wire.role);
    if (!wire.accountId || !role || !isFiniteInt(wire.vatAmountTetri)) {
        return null;
    }
    const amountTetri = Math.trunc(wire.vatAmountTetri);
    return {
        accountId: wire.accountId,
        accountCode: wire.code ?? '',
        accountName: wire.name ?? wire.code ?? '',
        role,
        amount: echoOf(amountTetri),
        amountTetri,
        entries: (wire.entries ?? [])
            .map(mapVatContributingEntry)
            .filter((entry): entry is VatContributingEntry => entry !== null),
    };
};

// ── warnings ────────────────────────────────────────────────────────────────

/**
 * Advisories are mapped leniently by design, exactly as `mapPlausibility` does:
 * a notice with no recognised rule still carries a message worth showing, and
 * refusing to show it would turn "we could not read this advisory" into silence.
 * Only a notice with no message is dropped — there would be nothing to display.
 *
 * Unexercised against the live API, which sends no warnings field at all.
 */
const mapVatWarning = (wire: VatWarningWire): VatWarning | null =>
    wire.message
        ? {
              rule: wire.rule ?? '',
              message: wire.message,
              accountId: wire.accountId ?? null,
              accountCode: wire.accountCode ?? null,
          }
        : null;

// ── report ──────────────────────────────────────────────────────────────────

const sideOf = (
    lines: VatBreakdownLine[],
    totalTetri: number,
    role: VatSideRole,
): VatSide => {
    const sideLines = lines.filter((line) => line.role === role);
    return {
        totalTetri,
        total: echoOf(totalTetri),
        lines: sideLines,
    };
};

export type MappedVatReport = {
    report: VatReport;
    /** Account ids (or `'<unidentified>'`) the mapping refused to place. */
    droppedAccountIds: string[];
};

/**
 * Map a VAT report response.
 *
 * `null` only when the payload is not a report at all — no readable window and
 * no readable totals, which is what an error body or a wrong endpoint looks
 * like. A partially unreadable account list still yields a report, with the
 * unreadable rows named in `droppedAccountIds` rather than guessed at.
 */
export const mapVatReport = (wire: VatReportWire): MappedVatReport | null => {
    const hasWindow = Boolean(wire.dateFrom && wire.dateTo);
    const hasTotals =
        isFiniteInt(wire.outputVatTetri) &&
        isFiniteInt(wire.inputVatTetri) &&
        isFiniteInt(wire.netPayableTetri);
    if (!hasWindow || !hasTotals) {
        return null;
    }

    const droppedAccountIds: string[] = [];
    const lines: VatBreakdownLine[] = [];
    for (const accountWire of wire.accounts ?? []) {
        const line = mapVatBreakdownLine(accountWire);
        if (line) {
            lines.push(line);
        } else if (vatRoleOf(accountWire.role) !== 'NONE') {
            // A `NONE` account is not a drop — it never belonged to a side.
            droppedAccountIds.push(accountWire.accountId ?? '<unidentified>');
        }
    }

    const outputTetri = intOr(wire.outputVatTetri, 0);
    const inputTetri = intOr(wire.inputVatTetri, 0);
    const netTetri = intOr(wire.netPayableTetri, 0);
    const settlementBalanceTetri = intOr(wire.settlementAccountBalanceTetri, 0);

    return {
        report: {
            window: {
                from: wire.dateFrom as string,
                to: wire.dateTo as string,
            },
            locale: localeOf(wire.locale),
            output: sideOf(lines, outputTetri, 'OUTPUT'),
            input: sideOf(lines, inputTetri, 'INPUT'),
            // The server's own figure, carried through. See this file's header,
            // point 4.
            netTetri,
            net: echoOf(netTetri),
            settlementAccount: {
                accountCode: wire.settlementAccountCode ?? null,
                balance: echoOf(settlementBalanceTetri),
                balanceTetri: settlementBalanceTetri,
            },
            warnings: (wire.warnings ?? [])
                .map(mapVatWarning)
                .filter((warning): warning is VatWarning => warning !== null),
        },
        droppedAccountIds,
    };
};
